import { CommerceService } from "@/services/commerce"

const commerceApi = CommerceService.api()

/**
 * Method to retrieve items that have selection having item keys
 *
 * @param {array} items
 * @param {number} totalItems
 *
 * @return {Promise<object>|null}
 */
export const getItemsWithSelections = async (items, totalItems) => {
  // check if product has product selections
  let itemsWithSelections = await Promise.all(
    items.map(async (item, key) => {
      const { sku, productKey, quantity, additionalInformation, colorId, allowMultiple } = item
      const productSkuIncluded = await getProductSelections(sku)

      if (!productSkuIncluded) return null

      // we will also need an information from the product like name and tax category
      const product = (
        await commerceApi
          .products()
          .withKey({ key: `orphan-article-${sku}` })
          .get()
          .execute()
      )?.body

      if (!product?.masterData?.published) return null

      return {
        sku,
        productKey,
        quantity,
        productSelection: productSkuIncluded,
        name: product?.masterData?.current?.name,
        taxCategory: product?.taxCategory,
        additionalInformation,
        colorId,
        allowMultiple,
        discountPercent: item?.discountPercent,
        newPrice: item?.newPrice,
        sendProductText: item?.sendProductText,
        sendTechnicalManual: item?.sendTechnicalManual,
        additionalComment: item?.additionalComment,
        position: item?.position || totalItems + key + 1,
      }
    }),
  )

  itemsWithSelections = itemsWithSelections.filter((n) => n)

  return itemsWithSelections
}

/**
 * Gets product selections and returns array of selection ids
 *
 * @param {string} sku
 * @returns {Promise<array>}
 */
export const getProductSelections = async (sku) => {
  const productSelections = (
    await commerceApi
      .products()
      .withKey({ key: `orphan-article-${sku}` })
      .productSelections()
      .get()
      .execute()
  )?.body?.results

  // no product selections, no actions
  if (productSelections.length < 1) return null

  // need to check if product with this SKU has a selection
  let productSkuIncluded = productSelections.map(({ variantSelection, productSelection }) => {
    const { skus, type } = variantSelection
    const isIncluded = skus.includes(sku)

    // product is excluded
    if (type === "includeAllExcept" && isIncluded) return false
    // if product sku is in the list, return relevant information
    if (type === "includeOnly" && isIncluded) return productSelection.id
    // if product sku is not in the list, return relevant information
    if (type === "includeAllExcept" && !isIncluded) return productSelection.id

    // nothing was found, end everything
    return null
  })

  const hasExclusion = productSkuIncluded.includes(false)

  // is excluded
  if (hasExclusion) return null

  productSkuIncluded = productSkuIncluded.filter((n) => n)

  // no selections
  if (productSkuIncluded.length < 1) return null

  return productSkuIncluded
}
